import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { takeLatest, call, put, select } from "redux-saga/effects";
import YmsNotification from "utils/functions/notification";
import { translate } from "utils/intl/IntlProvider";
import { getError } from "utils/requests/api.errors";
import * as Api from "./vehicle-dashboard-api";
import { Types, Creators } from "./vehicle-dashboard-duck";
dayjs.extend(duration);

export const getFilterFromState = (state) => state.VehicleDashboard;
export const getFilterFromStateUserSession = (state) => state.UserSession;

export const onExtractRegisterData = (registersData) =>
  registersData?.items.map((item) => {
    return {
      id: item.id,
      vehiclePlate: item.vehiclePlate,
      eventTime: item.eventTime,
      lenghtOfStay: item.lenghtOfStay,
      vehicleStepStatus: item.vehicleStepStatus,
      saleChannelDescription: item.saleChannelDescription,
      saleChannelId: item.saleChannelId,
      process: item.process,
      local: item.eventType,
      serviceStartTime: item.serviceStartTime,
      serviceFinishTime: item.serviceFinishTime,
      hasActiveTag: item.hasActiveTag,
      vehicleType: item.trailerType,
      scheduleDate:
        item.serviceStartTime !== null && item.serviceFinishTime
          ? `${dayjs.utc(item.serviceStartTime).format("DD/MM HH:mm")} - ${dayjs.utc(item.serviceFinishTime).format("HH:mm")}`
          : null,
    };
  });

const parseTimeStringToMilliseconds = (timeString) => {
  if (!timeString) return 0;

  const parts = timeString.split(":");
  if (parts.length < 3) return 0;

  const daySplit = parts[0].split(".");
  let days = 0;
  let hours = 0;

  if (daySplit.length === 2) {
    days = parseInt(daySplit[0], 10);
    hours = parseInt(daySplit[1], 10);
  } else {
    hours = parseInt(parts[0], 10);
  }

  const minutes = parseInt(parts[1], 10);
  const secondsParts = parts[2].split(".");
  const seconds = parseInt(secondsParts[0], 10);
  const milliseconds = parseInt(secondsParts[1]?.substring(0, 3) || "0", 10);

  const totalMilliseconds = ((days * 24 + hours) * 60 * 60 + minutes * 60 + seconds) * 1000 + milliseconds;

  return totalMilliseconds;
};

const formatDurationToHoursAndMinutes = (milliseconds: number) => {
  if (!milliseconds || isNaN(milliseconds)) return "0h 0min";

  const duration = dayjs.duration(milliseconds);
  const totalMinutes = Math.floor(duration.asMinutes());
  const totalHours = Math.floor(totalMinutes / 60);
  const days = Math.floor(totalHours / 24);

  const hours = totalHours % 24;
  const minutes = totalMinutes % 60;

  const dayStr = days > 0 ? `${days}d ` : "";
  const hourStr = `${hours}h `;
  const minuteStr = `${minutes}min`;

  return `${dayStr}${hourStr}${minuteStr}`.trim();
};

export function sortByScheduleOrStay(data, statusId) {
  if (statusId === 1 || statusId === 8 || statusId === 2 || statusId === 3) {
    const withSchedule = data.filter((item) => item.serviceStartTime && item.serviceFinishTime);
    const withoutSchedule = data.filter((item) => !item.serviceStartTime || !item.serviceFinishTime);

    const sortedWithSchedule = withSchedule.sort((a, b) => dayjs(a.serviceStartTime).diff(dayjs(b.serviceStartTime)));
    const sortedWithoutSchedule = withoutSchedule.sort((a, b) => dayjs(a.eventTime).diff(dayjs(b.eventTime)));

    return [...sortedWithSchedule, ...sortedWithoutSchedule];
  }

  if (statusId === 5 || statusId === 6) {
    const withSchedule = data.filter((item) => item.serviceStartTime && item.serviceFinishTime);
    const withoutSchedule = data.filter((item) => !item.serviceStartTime || !item.serviceFinishTime);

    const sortedWithSchedule = withSchedule.sort((a, b) => dayjs(a.serviceStartTime).diff(dayjs(b.serviceStartTime)));

    const sortedWithoutSchedule = withoutSchedule.sort((a, b) => {
      const aStayMs = parseTimeStringToMilliseconds(a.lenghtOfStay);
      const bStayMs = parseTimeStringToMilliseconds(b.lenghtOfStay);
      return bStayMs - aStayMs;
    });

    return [...sortedWithSchedule, ...sortedWithoutSchedule];
  }

  return data;
}

export function* onFetchVehiclesAsync() {
  try {
    yield put(Creators.vehicleDashboardShowLoading());
    const vehicleDashboardState = yield select(getFilterFromState);
    const userSessionState = yield select(getFilterFromStateUserSession);
    const vehicleStepsStatusOptions = [
      { id: 8, description: "InRadius" },
      { id: 1, description: "VehicleArriving" },
      { id: 5, description: "InternQueue" },
      { id: 6, description: "InOperation" },
      { id: 2, description: "NotReleased" },
      { id: 3, description: "Released" },
    ];
    const resp = yield call(Api.getVehicleDashboard, {
      locationId: userSessionState.userData.locationId,
      filter: vehicleDashboardState.filterOptions.searchFilter,
      ascending: "isAscending",
      _size: 400,
      _page: 1,
      processIds: vehicleDashboardState.filterOptions.processId,
      saleChannelIds: vehicleDashboardState.filterOptions.saleChannelId,
    });

    const registersData = resp.data;
    const data = onExtractRegisterData(registersData);
    const statusesToSort = [1, 2, 3, 5, 6, 8];
    const releasedOrNotReleasedData = [];

    for (const vehicleStepStatus of vehicleStepsStatusOptions) {
      let filtered = data.filter((item) => item.vehicleStepStatus === vehicleStepStatus.id);
      if (statusesToSort.includes(vehicleStepStatus.id)) {
        filtered = sortByScheduleOrStay(filtered, vehicleStepStatus.id);
      }

      if (vehicleStepStatus.id === 8) {
        const withCheckin = filtered.filter((item) => item.eventTime);
        const withoutCheckin = filtered.filter((item) => !item.eventTime);

        yield put(Creators.vehicleDashboardFetchRegister("InRadius", withCheckin, withCheckin.length, 0));
        yield put(Creators.vehicleDashboardFetchRegister("InRadiusWithoutCheckin", withoutCheckin, withoutCheckin.length, 0));
      } else if ([2, 3].includes(vehicleStepStatus.id)) {
        releasedOrNotReleasedData.push(...filtered);
      } else {
        if (statusesToSort.includes(vehicleStepStatus.id)) {
        }
        let totalMilliseconds = 0;
        let validItemsCount = 0;

        filtered.forEach((item) => {
          const stayTime = item.lenghtOfStay;
          if (stayTime != null) {
            totalMilliseconds += parseTimeStringToMilliseconds(stayTime);
            validItemsCount++;
          }
        });

        const averageStayMs = validItemsCount > 0 ? totalMilliseconds / validItemsCount : 0;
        const averageStay = formatDurationToHoursAndMinutes(averageStayMs);

        yield put(Creators.vehicleDashboardFetchRegister(vehicleStepStatus.description, filtered, filtered.length, averageStay));
      }
    }
    if (releasedOrNotReleasedData.length > 0) {
      const sortedReleasedOrNotReleased = sortByScheduleOrStay(releasedOrNotReleasedData, 2);
      yield put(Creators.vehicleDashboardFetchRegister("ReleasedOrNotReleased", sortedReleasedOrNotReleased, sortedReleasedOrNotReleased.length, 0));
    }
  } catch (error) {
    console.error(error);
    yield put(Creators.vehicleDashboardResetVehiclesData());
    const requestErrorMessage = yield getError(error) ?? translate("could_not_load_summary");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  } finally {
    yield put(Creators.vehicleDashboardHideLoading());
  }
}

export default function* VehicleDashboardSagas() {
  yield takeLatest(Types.VEHICLE_DASHBOARD_FETCH_REGISTER_ASYNC, onFetchVehiclesAsync);
}
